import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { AABB } from '../../engine/math/aabb';
import { Rng } from '../../engine/math/random';
import { translate, setEmissive } from '../../engine/geo/mesh';
import { box, cylinder, plane, combine } from '../../engine/geo/primitives';
import type { Ctx } from '../Ctx';
import { PALETTE, SPOTS, BIOMES } from '../world/Layout';
import { streamDist, worldHeight } from '../world/Terrain';
import { instanced, jitterInstance, treeMesh, hedgeMesh, grassMesh, standingStoneMesh, Instance } from '../world/Props';

/** Hearth Meadow (§2.1): hedgerows, stream, stone circle, signpost, trees, grass. Safe room. */
export function buildHearth(ctx: Ctx): void {
  const P = PALETTE.hearth;
  const rng = new Rng(101);
  const st = ctx.statics;
  const c = BIOMES.hearth.center, R = BIOMES.hearth.radius;
  const h = (x: number, z: number) => worldHeight(x, z);
  const clearOf = (x: number, z: number) => {
    if (Math.hypot(x - SPOTS.goldKeep.x, z - SPOTS.goldKeep.z) < 20) return false;
    if (Math.hypot(x - SPOTS.stoneCircle.x, z - SPOTS.stoneCircle.z) < 12) return false;
    if (Math.hypot(x - SPOTS.spawn.x, z - SPOTS.spawn.z) < 6) return false;
    if (streamDist(x, z) < 5) return false;
    if (Math.abs(z) < 7 && Math.abs(x) > 45) return false; // corridors east/west
    if (Math.abs(x) < 7 && z < -45) return false; // corridor north
    return true;
  };

  // trees
  const trees: Instance[] = [];
  for (let i = 0; i < 46; i++) {
    const a = rng.range(0, Math.PI * 2), r = rng.range(22, R - 4);
    const x = c.x + Math.cos(a) * r, z = c.z + Math.sin(a) * r;
    if (!clearOf(x, z)) continue;
    const inst = jitterInstance(rng, new Vec3(x, h(x, z) - 0.3, z), 0.12);
    trees.push(inst);
    st.addCylinder(x, z, 0.5 * (inst.scale as number), h(x, z) - 1, h(x, z) + 8, 'trunk');
  }
  instanced(ctx, 'hearth.trees', treeMesh(P[2], [0.3, 0.55, 0.25], 7, 0.45, 2.6, 3), trees, { wind: 0.25 });

  // hedgerows: a few lines
  const hedges: Instance[] = [];
  const hedgeLines: [Vec3, Vec3][] = [
    [new Vec3(-20, 0, 40), new Vec3(10, 0, 44)],
    [new Vec3(30, 0, -20), new Vec3(52, 0, -34)],
    [new Vec3(-38, 0, -30), new Vec3(-16, 0, -44)],
    [new Vec3(14, 0, 52), new Vec3(40, 0, 46)],
  ];
  for (const [a, b] of hedgeLines) {
    const n = Math.max(1, Math.round(a.distanceTo(b) / 4.2));
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const x = a.x + (b.x - a.x) * t, z = a.z + (b.z - a.z) * t;
      if (!clearOf(x, z)) continue;
      const yaw = Math.atan2(b.x - a.x, b.z - a.z) + Math.PI / 2;
      hedges.push({ pos: new Vec3(x, h(x, z) - 0.2, z), yaw, scale: rng.jitter(0.08) });
      st.addBox(AABB.fromMinMax(x - 2, h(x, z) - 1, z - 2, x + 2, h(x, z) + 1.3, z + 2), 'hedge');
    }
  }
  instanced(ctx, 'hearth.hedges', hedgeMesh([0.28, 0.48, 0.22], 4, 1.6, 1.4), hedges, { wind: 0.08 });

  // grass tufts
  const grass: Instance[] = [];
  for (let i = 0; i < 900; i++) {
    const x = c.x + rng.range(-R, R), z = c.z + rng.range(-R, R);
    if (Math.hypot(x - c.x, z - c.z) > R - 2) continue;
    if (Math.hypot(x - SPOTS.goldKeep.x, z - SPOTS.goldKeep.z) < 12) continue;
    if (streamDist(x, z) < 2.5) continue;
    grass.push({ pos: new Vec3(x, h(x, z) - 0.05, z), yaw: rng.range(0, 6.3), scale: rng.range(0.7, 1.4), tint: rng.chance(0.5) ? [1, 1, 1] : [0.9, 1.05, 0.8] });
  }
  instanced(ctx, 'hearth.grass', grassMesh([0.4, 0.62, 0.26]), grass, { wind: 0.9, castShadow: false });

  // stone circle: 8 standing stones, r=6
  const stones: Instance[] = [];
  const sc = SPOTS.stoneCircle;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const x = sc.x + Math.cos(a) * 6, z = sc.z + Math.sin(a) * 6;
    stones.push({ pos: new Vec3(x, h(x, z) - 0.2, z), yaw: a, scale: rng.jitter(0.12), tilt: rng.range(-0.08, 0.08) });
    st.addCylinder(x, z, 0.55, h(x, z) - 1, h(x, z) + 2.4, 'stone');
  }
  instanced(ctx, 'hearth.stones', standingStoneMesh([0.62, 0.6, 0.55], 2.4, 0.55), stones);
  // glowing runes ring on the circle floor
  const ring = setEmissive(translate(cylinder(5.2, 5.2, 0.08, 24, [0.95, 0.85, 0.55]), 0, 0, 0), [1, 0.85, 0.45], 0.35);
  const ringT = new Transform();
  ringT.position.set(sc.x, h(sc.x, sc.z) + 0.02, sc.z);
  ringT.snapPrev();
  ctx.spawn('hearth.ring', ringT, ring, { castShadow: false });

  // signpost near spawn: post + three arms pointing W, N, E
  const post = translate(cylinder(0.12, 0.14, 3, 7, P[2]), 0, 1.5, 0);
  const arm = (yaw: number, y: number) => {
    const a = translate(box(1.6, 0.28, 0.1, P[3]), 0.7, 0, 0);
    const t = new Transform(); t.rotation.setYaw(yaw); t.position.y = y;
    return translate(rotateYMesh(a, yaw), 0, y, 0);
  };
  const sp = new Vec3(SPOTS.spawn.x + 4, 0, SPOTS.spawn.z + 2);
  const signT = new Transform();
  signT.position.set(sp.x, h(sp.x, sp.z), sp.z);
  signT.snapPrev();
  ctx.spawn('hearth.signpost', signT, combine(post, arm(-Math.PI / 2, 2.6), arm(Math.PI, 2.2), arm(Math.PI / 2, 1.8)));
  st.addCylinder(sp.x, sp.z, 0.2, -1, 3, 'sign');

  // stream water plane (transparent, wobbling via wind)
  const water = plane(20, 110, 6, 40, [0.35, 0.6, 0.75]);
  setEmissive(water, [0.2, 0.35, 0.45], 0.15);
  const wt = new Transform();
  wt.position.set(-40, -0.45, 8);
  wt.rotation.setYaw(0.2);
  wt.snapPrev();
  ctx.spawn('hearth.stream', wt, water, { transparent: true, alpha: 0.55, castShadow: false, wind: 0.5 });

  // corridor mouths: a few boulders
  const boulders: Instance[] = [];
  for (const [x, z] of [[-60, 9], [-60, -9], [60, 9], [60, -9], [9, -60], [-9, -60]]) {
    boulders.push(jitterInstance(rng, new Vec3(x, h(x, z) - 0.3, z), 0.2));
    st.addCylinder(x, z, 1.3, h(x, z) - 1, h(x, z) + 1.6, 'boulder');
  }
  instanced(ctx, 'hearth.boulders', standingStoneMesh([0.6, 0.58, 0.52], 1.6, 1.4), boulders);
}

function rotateYMesh<T extends { positions: number[]; normals: number[] }>(m: T, yaw: number): T {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  for (let i = 0; i < m.positions.length; i += 3) {
    const x = m.positions[i], z = m.positions[i + 2];
    m.positions[i] = x * c + z * s; m.positions[i + 2] = -x * s + z * c;
    const nx = m.normals[i], nz = m.normals[i + 2];
    m.normals[i] = nx * c + nz * s; m.normals[i + 2] = -nx * s + nz * c;
  }
  return m;
}
